import { useEffect, useRef, useState } from "react";
import { Box, Button, Container, Typography } from "@mui/material";
import { Android } from "@mui/icons-material";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import PhoneModel from "../device/PhoneModel";
import { SCREENS } from "../screens/TreasureScreens";
import { prefersReducedMotion } from "../motion/useReducedMotion";
import { PLAY_STORE_URL } from "../../config/store";
import { trackEvent } from "../../utils/analytics";

gsap.registerPlugin(ScrollTrigger);

const SCENES = [
  { key: "home", cards: [["Balance", "₹25,739"], ["Income", "+₹1,95,000"], ["Expense", "−₹1,69,261"]] },
  { key: "add", cards: [["Added", "+ ₹185"], ["Category", "Food & Dining"], ["Saved", "In 3 taps"]] },
  { key: "transactions", cards: [["Every entry", "One list"], ["Latest", "Green tea ₹185"], ["This month", "42 entries"]] },
  { key: "reports", cards: [["Net savings", "+₹1,27,941"], ["Top category", "Investment"], ["Report", "Ready"]] },
];
const SCENE_MS = 3600;

const CARD_POS = [
  { top: "8%", left: { xs: "-2%", md: "-14%" } },
  { top: "44%", right: { xs: "-2%", md: "-16%" } },
  { bottom: "10%", left: { xs: "-2%", md: "-10%" } },
];

const glass = {
  bgcolor: "rgba(24,18,48,0.62)",
  border: "1px solid rgba(255,255,255,0.18)",
  backdropFilter: "blur(22px) saturate(1.4)",
  WebkitBackdropFilter: "blur(22px) saturate(1.4)",
  borderRadius: "16px",
  color: "#fff",
  boxShadow: "0 18px 40px rgba(0,0,0,0.35)",
};

const Hero = () => {
  const root = useRef(null);
  const phone = useRef(null);
  const [scene, setScene] = useState(0);
  const reduced = useRef(prefersReducedMotion());

  // entrance + scroll parallax
  useEffect(() => {
    if (reduced.current) return undefined;
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
      tl.from(".hero-aura", { opacity: 0, duration: 1.6 })
        .from(".hl", { yPercent: 110, duration: 0.9, stagger: 0.12 }, 0.2)
        .from(".hero-fade", { opacity: 0, y: 24, duration: 0.8, stagger: 0.1 }, 0.7)
        .from(phone.current, { y: 140, scale: 0.9, opacity: 0, rotateY: 28, duration: 1.3, ease: "expo.out" }, 0.6)
        .from(".hero-card", { scale: 0.6, opacity: 0, y: 20, duration: 0.7, stagger: 0.14, ease: "back.out(1.6)" }, 1.4);
      gsap.to(".hero-stage", { yPercent: -10, ease: "none", scrollTrigger: { trigger: root.current, start: "top top", end: "bottom top", scrub: true } });
      gsap.to(".hero-card", { y: (i) => -50 - i * 40, ease: "none", scrollTrigger: { trigger: root.current, start: "top top", end: "bottom top", scrub: true } });
    }, root);
    return () => ctx.revert();
  }, []);

  // screen rotation + phone turn
  useEffect(() => {
    if (reduced.current) return undefined;
    const id = setInterval(() => setScene((s) => (s + 1) % SCENES.length), SCENE_MS);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    if (reduced.current || scene === 0) return;
    gsap.fromTo(phone.current, { rotateY: 16, rotateX: 3 }, { rotateY: -8, rotateX: 0, duration: 1.3, ease: "expo.out", overwrite: "auto" });
  }, [scene]);

  return (
    <Box
      ref={root}
      component="section"
      aria-label="Hero"
      sx={{ position: "relative", overflow: "hidden", bgcolor: "#0B0B12", color: "#fff", minHeight: { md: "calc(100vh - 62px)" }, display: "flex", alignItems: "center", py: { xs: 6, md: 4 }, px: 2 }}
    >
      {/* aura */}
      <Box className="hero-aura" aria-hidden sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        <Box sx={{ position: "absolute", width: 720, height: 720, right: "-8%", top: "-12%", borderRadius: "50%", background: "radial-gradient(circle, rgba(126,87,194,0.55), transparent 65%)", filter: "blur(30px)", animation: "wxdrift 14s ease-in-out infinite alternate" }} />
        <Box sx={{ position: "absolute", width: 520, height: 520, left: "-10%", bottom: "-20%", borderRadius: "50%", background: "radial-gradient(circle, rgba(0,172,193,0.28), transparent 65%)", filter: "blur(40px)", animation: "wxdrift 18s ease-in-out infinite alternate-reverse" }} />
        <Box sx={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(rgba(255,255,255,0.07) 1px, transparent 1px)", backgroundSize: "28px 28px", maskImage: "radial-gradient(ellipse at 70% 40%, #000, transparent 70%)", WebkitMaskImage: "radial-gradient(ellipse at 70% 40%, #000, transparent 70%)" }} />
        <style>{`@keyframes wxdrift{from{transform:translate(0,0) scale(1)}to{transform:translate(-50px,40px) scale(1.12)}}
@keyframes wxfloat{from{transform:translateY(-7px)}to{transform:translateY(7px)}}
@keyframes wxcardin{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
@media (prefers-reduced-motion: reduce){*{animation:none!important}}`}</style>
      </Box>

      <Container maxWidth="lg" sx={{ position: "relative" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.05fr 0.95fr" }, gap: { xs: 8, md: 4 }, alignItems: "center" }}>
          <Box sx={{ textAlign: { xs: "center", md: "left" } }}>
            <Typography className="hero-fade" sx={{ color: "#B39DDB", fontWeight: 500, fontSize: "0.78rem", letterSpacing: "0.1em", textTransform: "uppercase", mb: 2.5 }}>
              Treasure · Personal money app
            </Typography>
            <Typography component="h1" sx={{ fontWeight: 600, fontSize: "clamp(2.6rem, 7vw, 5.2rem)", lineHeight: 1.04, letterSpacing: "-0.035em" }}>
              <Box component="span" sx={{ display: "block", overflow: "hidden", pb: 0.5 }}><Box component="span" className="hl" sx={{ display: "block" }}>Know where</Box></Box>
              <Box component="span" sx={{ display: "block", overflow: "hidden", pb: 1 }}>
                <Box component="span" className="hl" sx={{ display: "block", background: "linear-gradient(90deg,#B39DDB,#7E57C2 50%,#4DD0E1)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>your money goes.</Box>
              </Box>
            </Typography>
            <Typography className="hero-fade" sx={{ mt: 2.5, color: "rgba(255,255,255,0.68)", fontSize: { xs: "1.02rem", md: "1.2rem" }, lineHeight: 1.6, maxWidth: 480, mx: { xs: "auto", md: 0 } }}>
              Track income and expenses in a few taps. See reports build themselves. Built for pocket money and first salary.
            </Typography>
            <Box className="hero-fade" sx={{ mt: 4, display: "flex", gap: 2, justifyContent: { xs: "center", md: "flex-start" }, flexWrap: "wrap" }}>
              <Button
                component="a" href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" startIcon={<Android />}
                onClick={() => trackEvent("hero_google_play_click")}
                sx={{ bgcolor: "#fff", color: "#0B0B12", borderRadius: 999, textTransform: "none", fontWeight: 600, px: 3.5, py: 1.4, transition: "transform .25s, box-shadow .25s", boxShadow: "0 10px 30px rgba(126,87,194,0.35)", "&:hover": { bgcolor: "#fff", transform: "translateY(-2px) scale(1.03)", boxShadow: "0 16px 40px rgba(126,87,194,0.55)" } }}
              >
                Get it on Google Play
              </Button>
              <Button
                component="a" href="#how-it-works"
                sx={{ color: "#fff", borderRadius: 999, textTransform: "none", fontWeight: 500, px: 3, py: 1.4, border: "1px solid rgba(255,255,255,0.25)", "&:hover": { bgcolor: "rgba(255,255,255,0.08)" } }}
              >
                See how it works
              </Button>
            </Box>
          </Box>

          {/* phone stage */}
          <Box className="hero-stage" sx={{ position: "relative", display: "flex", justifyContent: "center", py: { xs: 2, md: 0 } }}>
            <Box sx={{ position: "relative" }}>
              <PhoneModel ref={phone} sx={{ width: { xs: 230, sm: 260, md: "min(290px, calc((100vh - 150px) * 0.46))" } }}>
                {SCENES.map((s, i) => {
                  const Screen = SCREENS[s.key];
                  const on = i === scene;
                  return (
                    <Box
                      key={s.key}
                      aria-hidden={!on}
                      sx={{
                        position: "absolute", inset: 0, bgcolor: "#fff", zIndex: on ? 2 : 1,
                        opacity: on ? 1 : 0,
                        transform: on ? "none" : "scale(1.03)",
                        // incoming screen fades in over the outgoing one, which vanishes only after it is covered
                        transition: on ? "opacity .6s ease, transform .9s cubic-bezier(.22,1,.36,1)" : "opacity 0s .7s, transform 0s .7s",
                      }}
                    >
                      <Screen active={on} />
                    </Box>
                  );
                })}
              </PhoneModel>
              {CARD_POS.map((pos, i) => (
                <Box key={i} className="hero-card" sx={{ position: "absolute", zIndex: 3, ...pos }}>
                  <Box sx={{ ...glass, px: 2, py: 1.25, minWidth: { xs: 112, md: 138 }, animation: `wxfloat ${5 + i}s ease-in-out ${i * 0.7}s infinite alternate` }}>
                    <Box key={scene} sx={{ animation: "wxcardin .6s ease both" }}>
                      <Typography sx={{ fontSize: "0.68rem", letterSpacing: "0.06em", textTransform: "uppercase", color: "rgba(255,255,255,0.6)" }}>{SCENES[scene].cards[i][0]}</Typography>
                      <Typography sx={{ fontSize: { xs: "0.9rem", md: "1.02rem" }, fontWeight: 600, whiteSpace: "nowrap" }}>{SCENES[scene].cards[i][1]}</Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Hero;
